// src/pages/BotRules/index.jsx
import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Paper,
  Grid,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Card,
  CardContent,
  CardHeader,
  Chip,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  ArrowForward as ArrowForwardIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import { PageContainer, ToolbarContainer } from "./styles";
import { botService, ruleService } from "@/services/api";

const RULE_TYPES = {
  line: "LINE 轉發",
  tg: "Telegram 轉發",
};

const BotRules = () => {
  const [sourceBots, setSourceBots] = useState([]);
  const [targetBots, setTargetBots] = useState([]);
  const [rules, setRules] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedSourceBot, setSelectedSourceBot] = useState("");
  const [selectedTargetBot, setSelectedTargetBot] = useState("");
  const [ruleType, setRuleType] = useState("");

  const [successMessage, setSuccessMessage] = useState("");

  // 載入機器人列表與規則
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // 獲取機器人列表
      const botsData = await botService.getBots();

      // 分類機器人
      const sourceBotsList = botsData.filter(
        (bot) => bot.notify_enabled === false
      );
      const targetBotsList = botsData.filter(
        (bot) => bot.notify_enabled === true
      );

      setSourceBots(sourceBotsList);
      setTargetBots(targetBotsList);

      // 獲取規則
      const rulesResponse = await ruleService.getRules(); //api.get("/bots/get_bot_rules");

      console.log(rulesResponse);

      setRules(rulesResponse.bot_rules || {});
    } catch (err) {
      setError(err.message || "載入數據失敗");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // 重置目標機器人，當規則類型變更時
  useEffect(() => {
    setSelectedTargetBot("");
  }, [ruleType]);

  // 過濾目標機器人列表，根據規則類型
  const filteredTargetBots = targetBots.filter((bot) => {
    if (!ruleType) return false;

    // 如果源是 LINE，目標應該也是 LINE
    if (ruleType === "line" && bot.bot_type === "LINE") return true;

    // 如果源是 TELEGRAM，目標應該也是 TELEGRAM
    if (ruleType === "tg" && bot.bot_type === "TELEGRAM") return true;

    return false;
  });

  // 添加規則
  const handleAddRule = async () => {
    if (!selectedSourceBot || !selectedTargetBot || !ruleType) {
      setError("請選擇源機器人和目標機器人");
      return;
    }

    try {
      setLoading(true);

      // 根據 API 結構發送請求
      /*await api.post("/bots/set_bot_rule", null, {
        params: {
          recv_callid: selectedSourceBot,
          type: ruleType,
          relay_callid: selectedTargetBot,
        },
      });*/

      await ruleService.setRule(selectedSourceBot, ruleType, selectedTargetBot);

      // 重新載入規則
      fetchData();

      // 清除選擇
      setSelectedSourceBot("");
      setRuleType("");
      setSelectedTargetBot("");
    } catch (err) {
      setError(err.message || "添加規則失敗");
    } finally {
      setLoading(false);
    }
  };

  // 清空規則
  const handleClearRules = async () => {
    if (!selectedSourceBot) {
      setError("請選擇要清空規則的源機器人");
      return;
    }

    try {
      setLoading(true);

      // 發送清空請求
      await ruleService.clearRules(selectedSourceBot);

      // 重新載入規則
      fetchData();

      // 提示成功
      setSuccessMessage(
        `已清空 ${getBotName(selectedSourceBot)} 的所有轉發規則`
      );

      // 清除選擇
      setSelectedSourceBot("");
      setRuleType("");
      setSelectedTargetBot("");
    } catch (err) {
      setError(err.message || "清空規則失敗");
    } finally {
      setLoading(false);
    }
  };

  // 取得機器人名稱
  const getBotName = (callbackId) => {
    const allBots = [...sourceBots, ...targetBots];
    const bot = allBots.find((b) => b.bot_callback_id === callbackId);
    return bot ? bot.bot_name : callbackId;
  };

  // 取得機器人類型
  const getBotType = (callbackId) => {
    const allBots = [...sourceBots, ...targetBots];
    const bot = allBots.find((b) => b.bot_callback_id === callbackId);
    return bot ? bot.bot_type : "未知";
  };

  if (loading && (sourceBots.length === 0 || targetBots.length === 0)) {
    return <LoadingSpinner />;
  }

  return (
    <PageContainer>
      <ToolbarContainer>
        <Typography variant="h6">機器人轉發規則管理</Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchData}
          disabled={loading}
        >
          重新整理
        </Button>
      </ToolbarContainer>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* 左側：新增規則區域 */}
        <Grid item xs={12} md={5}>
          <Card>
            <CardHeader title="新增轉發規則" />
            <CardContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                選擇一個來源機器人，將其消息轉發到目標機器人。
              </Typography>

              <Grid container spacing={2}>
                {/* 源機器人選擇 */}
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>源機器人 (停用通知)</InputLabel>
                    <Select
                      value={selectedSourceBot}
                      label="源機器人 (停用通知)"
                      onChange={(e) => setSelectedSourceBot(e.target.value)}
                    >
                      {sourceBots.map((bot) => (
                        <MenuItem key={bot.id} value={bot.bot_callback_id}>
                          {bot.bot_name} ({bot.bot_type})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* 規則類型選擇 */}
                <Grid item xs={12}>
                  <FormControl fullWidth disabled={!selectedSourceBot}>
                    <InputLabel>轉發類型</InputLabel>
                    <Select
                      value={ruleType}
                      label="轉發類型"
                      onChange={(e) => setRuleType(e.target.value)}
                    >
                      {Object.entries(RULE_TYPES).map(([key, value]) => (
                        <MenuItem key={key} value={key}>
                          {value}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* 目標機器人選擇 */}
                <Grid item xs={12}>
                  <FormControl fullWidth disabled={!selectedSourceBot}>
                    <InputLabel>目標機器人 (啟用通知)</InputLabel>
                    <Select
                      value={selectedTargetBot}
                      label="目標機器人 (啟用通知)"
                      onChange={(e) => setSelectedTargetBot(e.target.value)}
                    >
                      {filteredTargetBots.map((bot) => (
                        <MenuItem key={bot.id} value={bot.bot_callback_id}>
                          {bot.bot_name} ({bot.bot_type})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* 添加按鈕 */}
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={handleAddRule}
                    disabled={
                      !selectedSourceBot || !selectedTargetBot || loading
                    }
                    fullWidth
                  >
                    新增轉發規則
                  </Button>
                </Grid>

                {/* 清空規則按鈕 */}
                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={handleClearRules}
                    disabled={!selectedSourceBot || loading}
                    fullWidth
                  >
                    清空所有轉發規則
                  </Button>
                </Grid>

                {/* 成功訊息 */}
                {successMessage && (
                  <Grid item xs={12}>
                    <Alert
                      severity="success"
                      onClose={() => setSuccessMessage("")}
                      sx={{ mt: 2 }}
                    >
                      {successMessage}
                    </Alert>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* 右側：現有規則顯示區域 */}
        <Grid item xs={12} md={7}>
          <Card>
            <CardHeader title="現有轉發規則" />
            <CardContent>
              {Object.keys(rules).length === 0 ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  align="center"
                >
                  目前沒有轉發規則
                </Typography>
              ) : (
                Object.entries(rules).map(([sourceId, typeRules]) => (
                  <Box key={sourceId} sx={{ mb: 3 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: "bold", mb: 1 }}
                    >
                      源機器人: {getBotName(sourceId)} ({getBotType(sourceId)})
                    </Typography>

                    {Object.entries(typeRules).map(([type, targetIds]) => (
                      <Box key={type} sx={{ ml: 2, mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          {type === "line" ? "LINE 轉發" : "Telegram 轉發"}
                        </Typography>

                        <List dense>
                          {targetIds.map((targetId) => (
                            <ListItem
                              key={targetId}
                              sx={{
                                bgcolor: "background.paper",
                                mb: 1,
                                borderRadius: 1,
                              }}
                            >
                              <ListItemText
                                primary={getBotName(targetId)}
                                secondary={`ID: ${targetId}`}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    ))}

                    <Divider sx={{ my: 2 }} />
                  </Box>
                ))
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </PageContainer>
  );
};

export default BotRules;
